import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigationPermission } from '@/utils/navigationPermissions';

interface NavigationToolbarProps {
  onAddGroup: () => void;
  onAddItem: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onPreview?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  showUtilityActions?: boolean;
  showTreeToggles?: boolean;
}

export function NavigationToolbar({
  onAddGroup,
  onAddItem,
  onExpandAll,
  onCollapseAll,
  onPreview,
  onSave,
  isSaving = false,
  showUtilityActions = true,
  showTreeToggles = true,
}: NavigationToolbarProps) {
  const { t } = useTranslation('entity-portal');
  const canCreate = useNavigationPermission('MENU_CREATE');
  const canEdit = useNavigationPermission('MENU_EDIT');

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'center' }}
      spacing={1.5}
      sx={{ mb: 2.5 }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {canCreate ? (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRoundedIcon fontSize="small" />}
              onClick={onAddGroup}
              sx={{
                borderColor: 'divider',
                color: 'text.primary',
                px: 1.75,
              }}
            >
              {t('navigationConfig.addGroup')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRoundedIcon fontSize="small" />}
              onClick={onAddItem}
              sx={{
                borderColor: 'divider',
                color: 'text.primary',
                px: 1.75,
              }}
            >
              {t('navigationConfig.addItem')}
            </Button>
          </>
        ) : null}
      </Stack>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
        flexWrap="wrap"
      >
        {showTreeToggles ? (
          <>
            <Button
              variant="text"
              size="small"
              color="inherit"
              onClick={onExpandAll}
              sx={{ color: 'text.secondary', minWidth: 'auto' }}
            >
              {t('navigationConfig.expandAll')}
            </Button>
            <Box component="span" sx={{ color: 'text.disabled', px: 0.25 }}>
              /
            </Box>
            <Button
              variant="text"
              size="small"
              color="inherit"
              onClick={onCollapseAll}
              sx={{ color: 'text.secondary', minWidth: 'auto' }}
            >
              {t('navigationConfig.collapseAll')}
            </Button>
          </>
        ) : null}

        {showUtilityActions ? (
          <>
            {onPreview ? (
              <Button
                variant="outlined"
                size="small"
                onClick={onPreview}
                sx={{ borderColor: 'divider', ml: { sm: 1 } }}
              >
                {t('navigationConfig.previewMenu')}
              </Button>
            ) : null}
            {canEdit && onSave ? (
              <Button
                variant="contained"
                size="small"
                onClick={onSave}
                disabled={isSaving}
              >
                {t('navigationConfig.saveChanges')}
              </Button>
            ) : null}
          </>
        ) : null}
      </Stack>
    </Stack>
  );
}
